import '../registerAgGrid';
import { ThemeProvider } from '@mui/material';
import Box from '@mui/material/Box';
import { PspProvider } from '@opengov/app-core/psp/context/psp.context';
import { capitalMuiTheme } from '@opengov/capital-mui-theme';
import '@opengov/capital-mui-theme/dist/fonts.css';
import '@opengov/component-library-classic/classic/_styles.scss';
import type { ModuleMountOptions } from '@opengov/psp-contract';
import { ShellAuth0Provider, ShellAuth0ProviderProps } from '@opengov/shell-auth0-react';
import { QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { OgAnalyticsGate } from '../../application/instrument/OgAnalyticsGate';
import { FeatureFlagsProvider } from '../../application/providers/FeatureFlagsProvider';
import { queryClient } from '../../src/apiHooks/core/queryClient';
import { ToastProvider } from '../../src/components/Toast/contexts/Toast.provider';
import { AppProvider } from '../../src/context/AppContext';
import { getStandaloneAppAuth0Config } from '../../src/utils/auth';
import { styles } from './App.styles';
import '../../ovid.scss';
import { Router } from './Router';
import { useRouterStore } from './routerStore';

type Props = {
  pspOptions?: ModuleMountOptions;
};

export function App({ pspOptions }: Props) {
  const routerEntitySubdomain = useRouterStore((state) => state.params.entitySubdomain);
  const entityFriendlyId = pspOptions?.activeEntity?.friendlyId ?? routerEntitySubdomain;

  const auth0Props: ShellAuth0ProviderProps = pspOptions?.shell
    ? { mode: 'shell', shellBridge: pspOptions.shell, children: undefined }
    : { mode: 'standalone', ...getStandaloneAppAuth0Config() };

  return (
    <ShellAuth0Provider {...auth0Props}>
      <FeatureFlagsProvider entityFriendlyId={entityFriendlyId}>
        <OgAnalyticsGate mountOptions={pspOptions}>
          <ThemeProvider theme={capitalMuiTheme}>
            <QueryClientProvider client={queryClient}>
              <ToastProvider>
                <AppProvider>
                  <PspProvider pspOptions={pspOptions}>
                    <Box id="reporting-app-container" sx={styles.reportingAppContainer}>
                      <Router />
                    </Box>
                  </PspProvider>
                </AppProvider>
              </ToastProvider>
            </QueryClientProvider>
          </ThemeProvider>
        </OgAnalyticsGate>
      </FeatureFlagsProvider>
    </ShellAuth0Provider>
  );
}
